import { forwardRef, useRef, ReactNode } from "react";
import { Box, SystemProps } from "@storyofams/react-ui";
import { pick, omit } from "@styled-system/props";
import { rgba } from "polished";
import styled, { css } from "styled-components";
import { convertCSStoObject } from "~/context/helper";
import { useFlow } from "~/hooks";

interface RenderProps {
  checked: boolean;
  type: "checkbox" | "radio";
}

export interface OptionProps extends SystemProps {
  checked: boolean;
  disabled: boolean;
  onChange(checked: boolean): void;
  hasDescription?: boolean;
  /** @default not-transparent */
  imageType?: "not-transparent" | "white-bg";
  isImage?: boolean;
  name: string;
  render(props: RenderProps): ReactNode;
  type: "checkbox" | "radio";
  value: string;
  pageSettings?: any;
}

type WrapperProps = Pick<OptionProps, "checked" | "hasDescription" | "isImage" | "imageType" | "pageSettings"> & {
  pageSettings?: any;
};

const Wrapper = styled(Box).attrs<WrapperProps>((attrs) => ({
  flexDirection: attrs.isImage ? "column" : "row",
  ...attrs,
}))<WrapperProps>`
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  background-color: ${(p) => (p.isImage ? "transparent" : "white")};
  border: 1px solid;
  border-color: ${(p) => p.theme.colors.black20};
  border-radius: ${(p) => p.theme.radii.md};
  transition: background-color 0.18s ease-in-out, border-color 0.18s ease-in-out, box-shadow 0.18s ease-in-out;
  cursor: pointer;
  user-select: none;
  position: relative;

  ${(p) =>
    !p.isImage &&
    css`
      padding: ${p.hasDescription ? "19px" : "23px"} 24px;
    `}

  ${(p) => !p.checked && "&:hover,"}
  &:focus-within {
    &,
    .guided-selling__check {
      border-color: ${(p) => p.theme.colors.black60};
    }
  }

  ${(p) => !p.checked && "&:focus,&:active,"}
  &:focus-within {
    box-shadow: 0px 0px 0px 3px ${(p) => rgba(p.theme.colors.primary, 0.4)};
  }

  ${(p) =>
    p.checked &&
    css`
      && {
        border-color: ${p.theme.colors.black90};
        border-width: 2px;

        ${p.isImage
          ? css`
              > div {
                margin: -1px;
              }
            `
          : css`
              padding: ${p.hasDescription ? "18px" : "22px"} 23px;
            `}
      }

      &&::after {
        content: "";
        display: block;
        width: 16px;
        height: 16px;
        background-image: url(${p.pageSettings.checkIcon ?? "data:image/gif;base64,R0lGODlhAQABAAD/ACwAAAAAAQABAAACADs="});
        background-size: contain;
        background-repeat: no-repeat;
        position: absolute;
        top: 10px;
        right: 10px;
      }
    `}

  ${(p) =>
    p.checked &&
    p.isImage &&
    css`
      && {
        ${p.imageType === "white-bg"
          ? css`
              background-color: white;
            `
          : css`
              border-color: ${p.theme.colors.black60};
            `}
      }
    `}
`;

const Input = styled.input`
  position: absolute !important;
  top: 0 !important;
  left: 0 !important;
  margin: 0 !important;
  padding: 0 !important;
  width: 100% !important;
  height: 100% !important;
  opacity: 0 !important;
  z-index: 1 !important;
  cursor: inherit;
  box-shadow: none !important;
  outline: none !important;
`;

export const Option = forwardRef(({ checked, onChange, name, type, value, render, disabled, pageSettings, ...props }: OptionProps, forwardedRef: any) => {
  const inputRef = useRef<any>();
  const { flow } = useFlow();

  const handleClick = () => {
    onChange(type === "radio" ? true : !checked);

    window.requestAnimationFrame(() => {
      if (inputRef.current) {
        inputRef.current.focus();
      }
    });
  };

  const handleChange = (e) => {
    onChange(e.target.checked);
  };

  return (
    <Wrapper
      onClick={disabled ? () => {} : handleClick}
      checked={checked}
      pageSettings={pageSettings}
      style={{
        ...convertCSStoObject(flow?.cssEditor)[".answers"],
        ...convertCSStoObject(flow?.cssEditor)[".answer"],
        opacity: disabled ? 0.3 : 1,
        cursor: disabled ? "not-allowed" : "pointer",
        pointerEvents: disabled ? "none" : "auto",
      }}
      className={`answer ${checked ? "selected-answer" : ""} ${disabled ? "disabled-answer" : ""}`}
      {...pick(props)}
      isImage={props.isImage}
      hasDescription={props.hasDescription}
    >
      {render({ checked, ...omit(props) })}

      <Input
        checked={checked}
        type={type}
        id={value}
        disabled={disabled}
        value={value}
        name={name}
        ref={(ref) => {
          if (forwardedRef) {
            forwardedRef.current = ref;
          }
          inputRef.current = ref;
        }}
        onChange={disabled ? () => {} : handleChange}
      />
    </Wrapper>
  );
});
